import { useCallback, useEffect, useRef, useState } from 'react'
import i18n from '@/i18n'
import { notifyV2 } from '../components/v2/notify'

/**
 * The page states of the AD-209 editor:
 *
 *   mode   'setup'  — Step 1, "Ad Generation with AI" (new ads) with the placeholder preview;
 *          'editor' — Brand / Layout Design / Content (saved ads open here directly);
 *   tab    'editor' | 'chat' inside editor mode;
 *   banner the "needs a review" band after a draft run, until closed (success is only a toast);
 *   pinned whether the billboard strip sticks under the header.
 *
 * A generation run only counts as finished once the mounted preview has checked the result
 * (job status "completed"); that is when the page switches to the editor and announces it.
 */
export function useEditorFlow({ editor, initialAdId }) {
  const [mode, setMode] = useState(initialAdId ? 'editor' : 'setup')
  const [tab, setTab] = useState('editor')
  const [banner, setBanner] = useState(null)
  const [pinned, setPinned] = useState(true)
  const announcedJob = useRef(null)
  const job = editor.generationJob

  useEffect(() => {
    if (!job || job.status !== 'completed' || announcedJob.current === job.id) return
    announcedJob.current = job.id
    const review = job.quality === 'draft'
    setMode('editor')
    setBanner(review ? { id: job.id, variant: 'draft', message: job.issues?.length ? job.issues.join(' ') : null } : null)
    notifyV2(review
      ? {
        tone: 'warning',
        title: i18n.t('editorV2.shell.generated.reviewTitle'),
        message: (job.issues || []).join(' ') || i18n.t('editorV2.shell.generated.reviewMessage'),
      }
      : { tone: 'success', title: i18n.t('editorV2.shell.generated.title'), message: i18n.t('editorV2.shell.generated.message') })
  }, [job])

  // Saving from Step 1 (Continue) moves on to the editor. Only a save that happens while Step 1
  // is shown counts: an ad that was already saved when the user chose "Generate Ad with AI"
  // must stay in Step 1, otherwise the page bounced straight back and no run could start.
  const statusRef = useRef(editor.status)
  useEffect(() => {
    const previous = statusRef.current
    statusRef.current = editor.status
    if (editor.adId && mode === 'setup' && editor.status === 'saved' && previous !== 'saved') setMode('editor')
  }, [editor.adId, editor.status, mode])

  const openEditor = useCallback(() => {
    setMode('editor')
    setTab('editor')
  }, [])

  const backToSetup = useCallback(() => {
    setBanner(null)
    setTab('editor')
    setMode('setup')
  }, [])

  return {
    mode,
    tab,
    setTab,
    banner,
    closeBanner: () => setBanner(null),
    pinned,
    togglePin: () => setPinned((value) => !value),
    openEditor,
    backToSetup,
  }
}
